import React, { useState, useMemo } from 'react';
import { 
  Button, 
  Space, 
  Tag, 
  Dropdown, 
  Input,
  Tooltip,
  Avatar,
  Card,
  Typography,
  Row,
  Col,
  Badge,
  Select,
  Divider,
  App
} from 'antd';
import type { ColumnsType, TablePaginationConfig } from 'antd/es/table';
import type { SorterResult, FilterValue } from 'antd/es/table/interface';
import { 
  MoreOutlined, 
  EditOutlined, 
  DeleteOutlined,
  SearchOutlined,
  ReloadOutlined,
  CopyOutlined,
  FilterOutlined,
  BankOutlined,
  PlusOutlined
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { AllUser, UserOrInvitation, PendingInvitation } from '../hooks/useAllUsersAndInvitations';
import { SuperAdminUserService } from '../services/users';
import { SuperAdminInvitationService } from '../services/invitations';
import ResponsiveDataTable from '../components/ResponsiveDataTable';
import { useResponsive } from '@municipality/ui';
import styled from '@emotion/styled';

const SearchButtonContainer = styled.div<{ isMobile: boolean }>`
  display: flex;
  gap: 8px;
  width: ${({ isMobile }) => (isMobile ? '100%' : 'auto')};
  align-items: center;
`;

const SearchInput = styled(Input)`
  flex: 1;
  min-width: 0;
  text-align: left;
`;

const AddButton = styled(Button)`
  width: 32px;
  height: 32px;
  min-width: 32px;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  
  &:hover {
    transform: scale(1.1);
    box-shadow: 0 2px 8px rgba(24, 144, 255, 0.3);
  }
  
  &:active {
    transform: scale(0.95);
  }
`;

const UserCard = styled(Card)`
  width: 100%;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  transition: box-shadow 0.3s ease, transform 0.2s ease;
  margin-bottom: 12px;
  
  .ant-card-body {
    padding: 16px 12px;
  }
  
  &:hover {
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
    transform: translateY(-2px);
  }
`;

const TitleRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 8px;
  margin-bottom: 12px;
`;

const TitleContent = styled.div`
  flex: 1;
  min-width: 0;
`;

const ActionsContainer = styled.div`
  flex-shrink: 0;
`;

const ActionsButton = styled(Button)`
  min-width: 32px;
  height: 32px;
  background-color: #f5f5f5;
  transition: background-color 0.2s ease, transform 0.1s ease;
  
  &:hover {
    background-color: #e8e8e8;
    transform: scale(1.05);
  }
  
  &:active {
    transform: scale(0.95);
  }
`;

interface UserTableProps {
  users: UserOrInvitation[];
  loading: boolean;
  onEdit: (user: AllUser) => void;
  onAdd: () => void;
}

const UserTable: React.FC<UserTableProps> = ({ users, loading, onEdit, onAdd }) => {
  const { t } = useTranslation('users');
  const navigate = useNavigate();
  const { message, modal } = App.useApp();
  const { isMobile, isTablet } = useResponsive();
  const [searchText, setSearchText] = useState('');
  const [pageSize, setPageSize] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [filteredInfo, setFilteredInfo] = useState<Record<string, FilterValue | null>>({});
  const [sortedInfo, setSortedInfo] = useState<SorterResult<UserOrInvitation>>({});
  const [filterDropdownVisible, setFilterDropdownVisible] = useState(false);

  const handleDelete = (record: UserOrInvitation) => {
    console.log('🗑️ [v5] handleDelete called:', { type: record.type, id: record.id, email: record.email });
    
    if (record.type === 'user') {
      console.log('🗑️ [v5] User detected, scheduling confirmation modal...');
      // setTimeout decouples from Dropdown close event to prevent modal interference
      window.setTimeout(() => {
        console.log('🗑️ [v5] Opening modal.confirm (context-based, React 19 safe)');
        modal.confirm({
          title: t('deleteUser', 'Delete user'),
          content: t('deleteConfirm', 'Are you sure you want to delete this user? This action cannot be undone.'),
          okText: t('yes', 'Yes'),
          okType: 'danger',
          cancelText: t('cancel', 'Cancel'),
          onOk: async () => {
            try {
              await SuperAdminUserService.deleteUser(record.id);
              message.success(t('userDeleted', 'User deleted successfully'));
            } catch (error) {
              message.error(t('deleteError', 'Failed to delete user'));
              console.error('Delete user error:', error);
            }
          },
        });
      }, 150);
    } else if (record.type === 'invitation') {
      deleteInvitation(record);
    }
  };

  const deleteInvitation = async (record: UserOrInvitation) => {
    try {
      await SuperAdminInvitationService.deleteInvitation(record.id);
      message.success(t('invitationDeleted', 'Invitation deleted successfully'));
    } catch (error) {
      message.error(t('deleteInvitationError', 'Failed to delete invitation'));
      console.error('Delete invitation error:', error);
    }
  };


  const handleCopyEmail = async (email: string): Promise<void> => {
    try {
      // eslint-disable-next-line no-undef
      await navigator.clipboard.writeText(email);
      message.success(t('emailCopied', 'Email copied to clipboard!'));
    } catch (error) {
      console.error('Failed to copy email:', error);
      message.error(t('emailCopyError', 'Failed to copy email'));
    }
  };

  const getRoleColor = (role: string): string => {
    const colors: Record<string, string> = {
      super_admin: 'red',
      administrator: 'purple',
      region_manager: 'blue',
      content_manager: 'cyan',
      accountant: 'gold',
      support: 'green',
      municipality_admin: 'blue',
      municipality_user: 'green',
    };
    return colors[role?.toLowerCase()] || 'default';
  };

  const getStatusColor = (status: string): string => {
    const colors: Record<string, string> = {
      active: 'green',
      pending: 'orange',
      inactive: 'default',
      invited: 'blue',
    };
    return colors[status] || 'default';
  };

  const getInitials = (name?: string, surname?: string, displayName?: string): string => {
    if (name && surname) {
      return `${name.charAt(0).toUpperCase()}${surname.charAt(0).toUpperCase()}`;
    }
    if (name) return name.charAt(0).toUpperCase();
    if (displayName) {
      const parts = displayName.split(' ');
      const firstInitial = parts[0]?.charAt(0)?.toUpperCase() || '';
      const lastInitial = parts[1]?.charAt(0)?.toUpperCase() || '';
      return `${firstInitial}${lastInitial}` || 'U';
    }
    return 'U';
  };

  /** Get display role for role column - superAdminRole for super admins */
  const getDisplayRole = (record: UserOrInvitation): string => {
    if (record.type === 'invitation') {
      return (record as PendingInvitation).superAdminRole || 'super_admin';
    }
    return (record as AllUser).superAdminRole || (record as AllUser).role || 'super_admin';
  };

  /** Get display name for name column - only name/surname, never email */
  const getDisplayName = (record: UserOrInvitation): string => {
    if (record.type === 'invitation') {
      const inv = record as PendingInvitation;
      const fullName = [inv.name, inv.surname].filter(Boolean).join(' ').trim();
      return fullName || '—';
    }
    const userRecord = record as AllUser;
    const fullName = [userRecord.name, userRecord.surname].filter(Boolean).join(' ').trim();
    if (fullName) return fullName;
    const displayName = userRecord.displayName?.trim();
    if (displayName && !displayName.includes('@')) return displayName;
    if (userRecord.name) return userRecord.name;
    return '—';
  };

  // Step 1: Filter users based on search text
  const searchResults = useMemo(() => {
    if (!searchText.trim()) return users;
    
    const needle = searchText.toLowerCase();
    return users.filter((item) => {
      if (item.type === 'invitation') {
        const invitation = item as PendingInvitation;
        return (invitation.email?.toLowerCase().includes(needle));
      } else {
        const userItem = item as AllUser;
        const displayName = userItem.displayName?.toLowerCase() || '';
        const name = userItem.name?.toLowerCase() || '';
        return (name.includes(needle) ||
                displayName.includes(needle) ||
                userItem.email?.toLowerCase().includes(needle));
      }
    });
  }, [users, searchText]);

  // Get unique values for filters (use superAdminRole for super admin users/invitations)
  const uniqueRoles = useMemo(() => {
    return Array.from(new Set(searchResults.map((item: UserOrInvitation) => {
      return getDisplayRole(item);
    }))).filter(Boolean) as string[];
  }, [searchResults]);

  const uniqueStatuses = useMemo(() => {
    return Array.from(new Set(searchResults.map((item: UserOrInvitation) => {
      if (item.type === 'invitation') {
        return 'invited';
      }
      return (item as AllUser).status;
    }))).filter(Boolean) as string[];
  }, [searchResults]);

  // Step 2: Apply role and status filters to search results
  const displayedItems = useMemo(() => {
    let filtered = [...searchResults];

    // Apply role filter
    if (filteredInfo.role && (filteredInfo.role as string[]).length > 0) {
      const roleFilters = filteredInfo.role as string[];
      filtered = filtered.filter((item) => {
        const itemRole = getDisplayRole(item);
        return itemRole && roleFilters.includes(itemRole);
      });
    }

    // Apply status filter
    if (filteredInfo.status && (filteredInfo.status as string[]).length > 0) {
      const statusFilters = filteredInfo.status as string[];
      filtered = filtered.filter((item) => {
        const itemStatus = item.type === 'invitation' 
          ? 'invited' 
          : (item as AllUser).status;
        return itemStatus && statusFilters.includes(itemStatus);
      });
    }

    return filtered;
  }, [searchResults, filteredInfo]);

  // Count active filters
  const activeFilterCount = Object.values(filteredInfo).reduce((count, filter) => {
    if (filter && Array.isArray(filter) && filter.length > 0) {
      return count + filter.length;
    }
    return count;
  }, 0);

  // Handle filter changes
  const handleFilterChange = (filterType: 'role' | 'status', values: (string | number)[] | null) => {
    setFilteredInfo({
      ...filteredInfo,
      [filterType]: values && values.length > 0 ? values : null,
    });
  };

  const resetFilters = () => {
    setFilteredInfo({});
    setSortedInfo({});
    setCurrentPage(1);
    setSearchText('');
    setFilterDropdownVisible(false);
  };

  const handleTableChange = (
    pagination: TablePaginationConfig,
    filters: Record<string, FilterValue | null>,
    sorter: SorterResult<UserOrInvitation> | SorterResult<UserOrInvitation>[]
  ) => {
    setFilteredInfo(filters);
    setSortedInfo(Array.isArray(sorter) ? sorter[0] : sorter);
    setCurrentPage(pagination.current || 1);
  };

  // Custom card renderer for mobile and tablet
  const renderUserCard = (record: UserOrInvitation) => {
    const isInvitation = record.type === 'invitation';
    const name = getDisplayName(record);
    
    const email = record.email || '-';
    const role = getDisplayRole(record);
    const status = isInvitation ? 'invited' : (record as AllUser).status;
    const lastLogin = isInvitation 
      ? '-' 
      : (record as AllUser).lastLogin 
        ? new Date((record as AllUser).lastLogin as Date).toLocaleDateString() 
        : '-';

    // Tablet layout - all in one row
    if (isTablet && !isMobile) {
      return (
        <UserCard key={record.id} size="small">
          <TitleRow>
            <TitleContent>
              <Space size={8}>
                <Typography.Text strong>{name}</Typography.Text>
                <Tag color={getStatusColor(status)}>{t(`status.${status}`, status)}</Tag>
              </Space>
            </TitleContent>
            <ActionsContainer>
              <Dropdown
                menu={{
                  items: isInvitation ? [
                    {
                      key: 'delete',
                      label: t('deleteInvitation', 'Delete invitation'),
                      icon: <DeleteOutlined />,
                      onClick: () => handleDelete(record),
                      danger: true,
                    },
                  ] : [
                    {
                      key: 'edit',
                      label: t('editUser', 'Edit'),
                      icon: <EditOutlined />,
                      onClick: () => onEdit(record as AllUser),
                    },
                    {
                      key: 'municipalities',
                      label: t('manageMunicipalities', 'Manage municipalities'),
                      icon: <BankOutlined />,
                      onClick: () => navigate(`/users/${record.id}/municipalities`),
                    },
                    {
                      key: 'delete',
                      label: t('deleteUser', 'Delete user'),
                      icon: <DeleteOutlined />,
                      onClick: () => handleDelete(record),
                      danger: true,
                    },
                  ],
                }}
                trigger={['click']}
                placement="bottomRight"
                getPopupContainer={(trigger) => trigger.parentElement || document.body}
                overlayStyle={{ zIndex: 1050 }}
              >
                <ActionsButton 
                  type="text" 
                  icon={<MoreOutlined />} 
                  size="small"
                />
              </Dropdown>
            </ActionsContainer>
          </TitleRow>
          <Row gutter={[8, 8]}>
            <Col span={24}>
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>Email: </Typography.Text>
              <Space size={4}>
                <Typography.Text>{email}</Typography.Text>
                <Tooltip title={t('copyEmail', 'Copy email')}>
                  <Button
                    type="text"
                    size="small"
                    icon={<CopyOutlined />}
                    onClick={() => handleCopyEmail(email)}
                    style={{ padding: '0 4px', height: 'auto' }}
                  />
                </Tooltip>
              </Space>
            </Col>
            <Col span={12}>
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>Role: </Typography.Text>
              <Tag color={getRoleColor(role)}>{role?.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}</Tag>
            </Col>
            <Col span={12}>
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>Last login: </Typography.Text>
              <Typography.Text>{lastLogin}</Typography.Text>
            </Col>
          </Row>
        </UserCard>
      );
    }

    // Mobile layout - stacked vertically
    return (
      <UserCard key={record.id} size="small">
        <TitleRow>
          <TitleContent>
            <Space size={8} wrap>
              <Typography.Text strong style={{ fontSize: 16 }}>{name}</Typography.Text>
              <Tag color={getStatusColor(status)}>{t(`status.${status}`, status)}</Tag>
            </Space>
          </TitleContent>
          <ActionsContainer>
            <Dropdown
              menu={{
                items: isInvitation ? [
                  {
                    key: 'delete',
                    label: t('deleteInvitation', 'Delete invitation'),
                    icon: <DeleteOutlined />,
                    onClick: () => handleDelete(record),
                    danger: true,
                  },
                ] : [
                  {
                    key: 'edit',
                    label: t('editUser', 'Edit'),
                    icon: <EditOutlined />,
                    onClick: () => onEdit(record as AllUser),
                  },
                  {
                    key: 'municipalities',
                    label: t('manageMunicipalities', 'Manage municipalities'),
                    icon: <BankOutlined />,
                    onClick: () => navigate(`/users/${record.id}/municipalities`),
                  },
                  {
                    key: 'delete',
                    label: t('deleteUser', 'Delete user'),
                    icon: <DeleteOutlined />,
                    onClick: () => handleDelete(record),
                    danger: true,
                  },
                ],
              }}
              trigger={['click']}
              placement="bottomRight"
              getPopupContainer={(trigger) => trigger.parentElement || document.body}
              overlayStyle={{ zIndex: 1050 }}
            >
              <ActionsButton 
                type="text" 
                icon={<MoreOutlined />} 
                size="small"
              />
            </Dropdown>
          </ActionsContainer>
        </TitleRow>
        <Space direction="vertical" style={{ width: '100%' }} size={8}>
          <div>
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>Email: </Typography.Text>
            <Space size={4}>
              <Typography.Text>{email}</Typography.Text>
              <Tooltip title={t('copyEmail', 'Copy email')}>
                <Button
                  type="text"
                  size="small"
                  icon={<CopyOutlined />}
                  onClick={() => handleCopyEmail(email)}
                  style={{ padding: '0 4px', height: 'auto' }}
                />
              </Tooltip>
            </Space>
          </div>
          <div>
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>Role: </Typography.Text>
            <Tag color={getRoleColor(role)}>{role?.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}</Tag>
          </div>
          <div>
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>Last login: </Typography.Text>
            <Typography.Text>{lastLogin}</Typography.Text>
          </div>
        </Space>
      </UserCard>
    );
  };

  const colWidth = 180;
  const columns: ColumnsType<UserOrInvitation> = [
    {
      title: t('name', 'Name'),
      dataIndex: 'name',
      key: 'name',
      width: colWidth,
      ellipsis: true,
      render: (_: string, record: UserOrInvitation) => {
        const displayName = getDisplayName(record);
        const inv = record as PendingInvitation;
        const initials = record.type === 'invitation'
          ? ((inv.name || inv.surname)
              ? getInitials(inv.name, inv.surname)
              : '?')
          : getInitials((record as AllUser).name, (record as AllUser).surname, (record as AllUser).displayName);
        return (
          <Space>
            <Avatar size="small" style={{ backgroundColor: '#1890ff' }}>
              {initials}
            </Avatar>
            <span>{displayName}</span>
          </Space>
        );
      },
      sorter: (a: UserOrInvitation, b: UserOrInvitation) => {
        if (a.type === 'invitation' || b.type === 'invitation') return 0;
        const aName = getDisplayName(a);
        const bName = getDisplayName(b);
        return aName.localeCompare(bName);
      },
      sortOrder: sortedInfo.columnKey === 'name' ? sortedInfo.order : null,
    },
    {
      title: t('email', 'Email'),
      dataIndex: 'email',
      key: 'email',
      width: colWidth,
      ellipsis: true,
      render: (_: string, record: UserOrInvitation) => {
        const email = record.email;
        if (!email) return '-';
        return (
          <Space>
            <span>{email}</span>
            <Tooltip title={t('copyEmail', 'Copy email')}>
              <Button
                type="text"
                size="small"
                icon={<CopyOutlined />}
                onClick={() => handleCopyEmail(email)}
                style={{ padding: '0 4px', height: 'auto' }}
              />
            </Tooltip>
          </Space>
        );
      },
      sorter: (a: UserOrInvitation, b: UserOrInvitation) => {
        const aEmail = a.email || '';
        const bEmail = b.email || '';
        return aEmail.localeCompare(bEmail);
      },
      sortOrder: sortedInfo.columnKey === 'email' ? sortedInfo.order : null,
    },
    {
      title: t('role', 'Role'),
      dataIndex: 'superAdminRole',
      key: 'role',
      width: colWidth,
      render: (_: string, record: UserOrInvitation) => {
        const role = getDisplayRole(record);
        return (
          <Tag color={getRoleColor(role)}>
            {t(`${role}`, role.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()))}
          </Tag>
        );
      },
      filters: uniqueRoles.map(role => ({
        text: t(`${role}`, role.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())),
        value: role,
      })),
      filteredValue: filteredInfo.role || null,
      onFilter: (value: boolean | React.Key, record: UserOrInvitation) => {
        return getDisplayRole(record) === value;
      },
    },
    {
      title: t('status', 'Status'),
      dataIndex: 'status',
      key: 'status',
      width: colWidth,
      render: (status: string, record: UserOrInvitation) => {
        if (record.type === 'invitation') {
          return (
            <Tag color="blue">
              {t('invited', 'Invited')}
            </Tag>
          );
        }
        return (
          <Tag color={getStatusColor(status)}>
            {t(`${status}`, status)}
          </Tag>
        );
      },
      filters: uniqueStatuses.map(status => ({
        text: t(`${status}`, status),
        value: status,
      })),
      filteredValue: filteredInfo.status || null,
      onFilter: (value: boolean | React.Key, record: UserOrInvitation) => {
        if (record.type === 'invitation') {
          return value === 'invited';
        }
        return (record as AllUser).status === value;
      },
    },
    {
      title: t('lastLogin', 'Last Login'),
      dataIndex: 'lastLogin',
      key: 'lastLogin',
      width: 120,
      render: (_: Date | undefined, record: UserOrInvitation) => {
        if (record.type === 'invitation') {
          return '-';
        }
        const userRecord = record as AllUser;
        return userRecord.lastLogin ? new Date(userRecord.lastLogin).toLocaleDateString() : '-';
      },
      sorter: (a: UserOrInvitation, b: UserOrInvitation) => {
        if (a.type === 'invitation' || b.type === 'invitation') return 0;
        const aUser = a as AllUser;
        const bUser = b as AllUser;
        if (!aUser.lastLogin && !bUser.lastLogin) return 0;
        if (!aUser.lastLogin) return 1;
        if (!bUser.lastLogin) return -1;
        return new Date(aUser.lastLogin).getTime() - new Date(bUser.lastLogin).getTime();
      },
      sortOrder: sortedInfo.columnKey === 'lastLogin' ? sortedInfo.order : null,
    },
    {
      title: t('actions', 'Actions'),
      key: 'actions',
      width: 80,
      align: 'center' as const,
      render: (_: unknown, record: UserOrInvitation) => {
        if (record.type === 'invitation') {
          return (
            <Dropdown
              menu={{
                items: [
                  {
                    key: 'delete',
                    label: t('deleteInvitation', 'Delete Invitation'),
                    icon: <DeleteOutlined />,
                    onClick: () => handleDelete(record),
                    danger: true,
                  },
                ],
              }}
              trigger={['click']}
            >
              <Button type="text" icon={<MoreOutlined />} />
            </Dropdown>
          );
        }

        const userRecord = record as AllUser;
        
        return (
          <Dropdown
            menu={{
              items: [
                {
                  key: 'edit',
                  label: t('editUser', 'Edit'),
                  icon: <EditOutlined />,
                  onClick: () => onEdit(userRecord),
                },
                {
                  key: 'municipalities',
                  label: t('manageMunicipalities', 'Manage municipalities'),
                  icon: <BankOutlined />,
                  onClick: () => navigate(`/users/${record.id}/municipalities`),
                },
                {
                  key: 'delete',
                  label: t('deleteUser', 'Delete User'),
                  icon: <DeleteOutlined />,
                  onClick: () => handleDelete(record),
                  danger: true,
                },
              ],
            }}
            trigger={['click']}
          >
            <Button type="text" icon={<MoreOutlined />} />
          </Dropdown>
        );
      },
    },
  ];

  return (
    <div>
      {/* Search and Filters */}
      <div style={{ marginBottom: 16 }}>
        <SearchButtonContainer isMobile={isMobile}>
          <SearchInput
            placeholder={t('searchUsers', 'Search users...')}
            allowClear
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            suffix={<SearchOutlined />}
          />
          <Dropdown
            open={filterDropdownVisible}
            onOpenChange={setFilterDropdownVisible}
            trigger={['click']}
            placement="bottomRight"
            popupRender={() => (
              <div style={{
                background: '#fff',
                borderRadius: '8px',
                padding: '16px',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                minWidth: '280px',
                maxWidth: '320px'
              }}>
                <div style={{ marginBottom: '12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontWeight: 600, fontSize: '16px' }}>{t('filters', 'Filters')}</span>
                  <Button
                    type="text"
                    size="small"
                    icon={<ReloadOutlined />}
                    onClick={resetFilters}
                    style={{ fontSize: '12px' }}
                  >
                    {t('reset', 'Reset')}
                  </Button>
                </div>
                <Divider style={{ margin: '12px 0' }} />

                {/* Role Filter */}
                <div style={{ marginBottom: '16px' }}>
                  <div style={{ fontWeight: 500, marginBottom: '8px', fontSize: '14px' }}>{t('role', 'Role')}</div>
                  <Select
                    mode="multiple"
                    placeholder={t('selectRoles', 'Select roles')}
                    value={filteredInfo.role as string[] || []}
                    onChange={(values) => handleFilterChange('role', values)}
                    style={{ width: '100%' }}
                    size="small"
                    allowClear
                    maxTagCount="responsive"
                  >
                    {uniqueRoles.map((role) => (
                      <Select.Option key={role} value={role}>
                        {t(`${role}`, role.replace('_', ' ').toUpperCase())}
                      </Select.Option>
                    ))}
                  </Select>
                </div>

                <Divider style={{ margin: '12px 0' }} />

                {/* Status Filter */}
                <div style={{ marginBottom: '8px' }}>
                  <div style={{ fontWeight: 500, marginBottom: '8px', fontSize: '14px' }}>{t('status', 'Status')}</div>
                  <Select
                    mode="multiple"
                    placeholder={t('selectStatuses', 'Select statuses')}
                    value={filteredInfo.status as string[] || []}
                    onChange={(values: string[]) => handleFilterChange('status', values)}
                    style={{ width: '100%' }}
                    size="small"
                    allowClear
                    maxTagCount="responsive"
                  >
                    {uniqueStatuses.map((status) => (
                      <Select.Option key={status} value={status}>
                        {t(`${status}`, status)}
                      </Select.Option>
                    ))}
                  </Select>
                </div>
              </div>
            )}
          >
            <Button
              type={activeFilterCount > 0 ? 'default' : 'text'}
              icon={
                <Badge count={activeFilterCount} size="small" offset={[-2, 2]}>
                  <FilterOutlined />
                </Badge>
              }
              onClick={() => setFilterDropdownVisible(!filterDropdownVisible)}
            />
          </Dropdown>
          <AddButton
            type="primary"
            icon={<PlusOutlined />}
            onClick={onAdd}
          />
        </SearchButtonContainer>
      </div>
      
      <ResponsiveDataTable
        dataSource={displayedItems}
        columns={columns}
        loading={loading}
        pagination={{
          current: currentPage,
          pageSize,
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} ${t('users', 'users')}`,
          onChange: setCurrentPage,
          onShowSizeChange: (_, size) => {
            setPageSize(size);
            setCurrentPage(1);
          },
        }}
        rowKey="id"
        maxMobileColumns={3}
        onChange={handleTableChange}
        customCardRenderer={renderUserCard}
      />
    </div>
  );
};

export default UserTable;
